import Vue from 'vue';
import { debounce } from '@/helpers/debounce';

/**
 * Observable state to track mobile device detection and caching
 * - storeDesignerDevice: Tracks the device type in store designer mode
 * - isMobileCache: Caches the result of mobile device detection to avoid repeated calculations
 * - windowWidth: Tracks window width to detect changes that require cache invalidation
 */
const state = Vue.observable({
    storeDesignerDevice:
        typeof window !== 'undefined' && window.storeDesigner
            ? window.storeDesignerDevice
            : null,
    isMobileCache: null,
    windowWidth: typeof window !== 'undefined' ? window.innerWidth : 0
});

if (typeof window !== 'undefined') {
    Object.defineProperty(window, 'storeDesignerDevice', {
        set(value) {
            state.storeDesignerDevice = value;
            state.isMobileCache = null;
        },
        get() {
            return state.storeDesignerDevice;
        },
        configurable: true
    });

    /**
     * Debounced resize handler to prevent excessive recalculations
     * Invalidates the mobile device cache when window width changes
     */
    const handleResize = debounce(() => {
        if (state.windowWidth !== window.innerWidth) {
            state.windowWidth = window.innerWidth;
            state.isMobileCache = null;
        }
    }, 250); // 250ms debounce time

    window.addEventListener('resize', handleResize);
}

/**
 * Performs the actual check to determine if the current device is mobile
 * Uses multiple detection methods for better accuracy:
 * 1. User agent string matching
 * 2. Navigator userAgentData API (if available)
 * 3. Touch capability detection
 * 4. Media query for coarse pointer
 *
 * @returns {boolean} True if the device is detected as mobile
 */
const checkIfMobileDevice = () => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
        return false;
    }

    if (window.storeDesigner) {
        return state.storeDesignerDevice === 'mobile';
    }

    return (
        /Mobi|iP(hone|od|ad)|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(
            navigator.userAgent
        ) ||
        navigator.userAgentData?.mobile ||
        'ontouchstart' in document.documentElement ||
        window.matchMedia('(pointer:coarse)').matches
    );
};

/**
 * Optimized function to check if the current device is mobile
 * Uses caching to avoid repeated calculations, improving performance
 * Cache is invalidated when:
 * 1. storeDesignerDevice changes
 * 2. Window width changes (via debounced resize handler)
 *
 * @returns {boolean} True if the device is detected as mobile
 */
const isMobile = () => {
    if (state.isMobileCache === null) {
        state.isMobileCache = checkIfMobileDevice();
    }
    return state.isMobileCache;
};

export default isMobile;
